import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import * as moment from 'moment';
import { tap } from 'rxjs/operators';
import { FormGroup } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class PersistenceService {

  private _procedureStarted: BehaviorSubject<string> = new BehaviorSubject(null);
  procedureStarted$: Observable<string> = this._procedureStarted.asObservable();

  private _commandStateArray: BehaviorSubject<CommandShareArrayWrapper> = new BehaviorSubject(null);
  commandStateArray$: Observable<CommandShareArrayWrapper> = this._commandStateArray.asObservable();

  actions: ServerCommand<any, any>[] = [];
  archive: ServerCommand<any, any>[] = [];

  private _onCommandDone: Subject<ServerCommand<any, any>> = new Subject();
  public onCommandDone$: Observable<ServerCommand<any, any>> = this._onCommandDone.asObservable();

  private _onAllCommandsDone: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public onAllCommandsDone$: Observable<boolean> = this._onAllCommandsDone.asObservable();

  constructor() {
    this._onCommandDone.subscribe(
      (command: ServerCommand<any, any>) => {
        command.state.complete();
        this.executePendingCommands(command.transaction);
      }
    );
    this._onAllCommandsDone.subscribe((value) => {
      if (value) this._procedureStarted.next(null);
    });
  }

  addCommand(command: ServerCommand<any, any>) {
    this.actions.push(command);
    command.state.subscribe(() => this.updateCommandShareArray(command.transaction));
    this._onAllCommandsDone.next(false);
  }

  addCommands(commands: ServerCommand<any, any>[]) {
    for (let command of commands) this.addCommand(command);
  }

  clear() {
    this.archive = this.actions;
    this.actions = [];
    this._procedureStarted.next(null);
    this._commandStateArray.next(null);
    this._onAllCommandsDone.next(null);
  }

  clearTransaction(transactionId: string) {
    var action = this.actions.find(x => x.transaction === transactionId);
    while (action) {
      this.archive.push(action);
      this.actions.splice(this.actions.indexOf(action), 1);
      action = this.actions.find(x => x.transaction === transactionId);
    }
  }

  getPendingCommands(transaction: string) {
    return this.actions.filter(
      x => x.transaction === transaction && !x.isDone() && !x.requirements.some(
        y => !this.actions.find(z => z.guid === y.guid).isDone()
      )
    );
  }

  countTransactionCommands(transactionGuid: string) {
    return this.actions.filter(x => x.transaction === transactionGuid).length;
  }

  executePendingCommands(transaction: string) {
    if (this._procedureStarted.value !== transaction) this._procedureStarted.next(transaction);

    let pendingCommands = this.getPendingCommands(transaction);

    if (pendingCommands.length === 0) {
      this._onAllCommandsDone.next(true);
      return;
    } else {
      this._onAllCommandsDone.next(false);
    }
    for (let command of pendingCommands) {
      if (command.state.value === ServerCommandState.Started) continue;
      command.started = moment();
      command.state.next(ServerCommandState.Started);
      command
        .action()
        .subscribe(
          (result: any) => {
            command.successResponse = result ? result : true;
            for (let callback of flatten<RequirementWrapper<any>>(this.actions.map(x => x.requirements.filter(x => x.guid === command.guid)))) {
              callback
                .onSucceed
                .next(result);
            }
            command.finished = moment();
            command.state.next(ServerCommandState.Succeeded);
            this._onCommandDone.next(command);
          },
          (error: any) => {
            command.errorResponse = { error: error };
            command.state.next(ServerCommandState.Failed);
            this.markSuccessorsAsFailed(command, command);
            this.executePendingCommands(command.transaction);
          }
        );
    }
  }

  markSuccessorsAsFailed(command: ServerCommand<any, any>, root: ServerCommand<any, any>) {
    let successors = this.actions.filter(x => x.requirements.map(x => x.guid).includes(command.guid));
    for (let successor of successors) this.markSuccessorsAsFailed(successor, root);
    if (command !== root) {
      command.errorResponse = { anchestorFailed: root };
      command.state.next(ServerCommandState.Failed);
    }
  }

  tryRemoveCommand(command: ServerCommand<any, any>) {
    let i = this.actions.indexOf(command);
    if (i > -1) this.actions.splice(i, 1);
  }

  updateCommandShareArray(transaction: string) {
    var pending = 0, succeeded = 0, failed = 0;
    for (let command of this.actions.filter(x => x.transaction === transaction)) {
      switch (command.state.value) {
        case ServerCommandState.Initial:
        case ServerCommandState.Started:
          pending++;
          break;
        case ServerCommandState.Succeeded:
          succeeded++;
          break;
        case ServerCommandState.Failed:
          failed++;
          break;
      }
    }
    this._commandStateArray.next({
      'transaction': transaction,
      'pending': pending,
      'succeeded': succeeded,
      'failed': failed
    } as CommandShareArrayWrapper);
  }
}

export class CommandShareArrayWrapper {
  transaction: string;
  pending: number;
  succeeded: number;
  failed: number;
}

export const ServerCommandState = {
  "Initial": 0,
  "Started": 1,
  "Succeeded": 2,
  "Failed": 3
}

export class RequirementWrapper<T> {
  guid: string;
  isSucceeded: boolean = false;
  onSucceed: ReplaySubject<T> = new ReplaySubject<T>(null);
  constructor(guid: string, callback: (object: T) => void) {
    this.guid = guid;
    this
      .onSucceed
      .pipe(tap(() => this.isSucceeded = true))
      .subscribe((value) => {
        callback(value);
        this.dispose();
      });
  }
  dispose() {
    this.onSucceed.complete();
  }
}

export class ServerCommand<T, E> {
  transaction?: string;
  identifier?: string;
  successResponse: T;
  errorResponse: E;
  started: moment.Moment;
  finished: moment.Moment;
  guid: string;
  requirements: RequirementWrapper<any>[];
  action: () => Observable<T>;
  formGroup?: FormGroup;
  title: string;
  state: BehaviorSubject<number>;
  onStateChange$: Observable<number>;
  constructor(title: string, action: () => Observable<T>, requirements?: RequirementWrapper<any>[], transaction?: string, identifier?: string) {
    this.guid = Guid.newGuid();
    this.action = action.bind(this);
    this.requirements = [];
    this.title = title;
    this.state = new BehaviorSubject(ServerCommandState.Initial);
    this.onStateChange$ = this.state.asObservable();
    if (Array.isArray(requirements)) this.requirements = requirements;
    else this.requirements = [];
    if (transaction) this.transaction = transaction;
    if (identifier) this.identifier = identifier;
  }
  isDone() {
    return this.errorResponse || this.successResponse;
  }
}

export function flatten<T>(array: any[]): T[] {
  return array.reduce(function (flat, toFlatten) {
    return flat.concat(Array.isArray(toFlatten) ? flatten(toFlatten) : toFlatten);
  }, []);
}

export class Guid {
  static newGuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0,
        v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}
